import { z } from 'zod';
import type { CpTerms } from './calc.js';
import type { CpSource } from './charter-party.js';
import type { ParseResult, SchemaIssue } from './schemas.js';

/**
 * Seferin performans referansı (ADR 0084 Ek 2; sahip kararı 2026-10-20, ortak isteği). Analiz
 * gerçekleşeni bir referansla karşılaştırır:
 *
 * 1. **Charter party:** seferin CP'si (`voyageCharterParty`) hız ya da tüketim şartı taşıyorsa.
 * 2. **Owner's reference:** ofisin girdiği hız ve tüketim (laden/ballast); gemi bazında varsayılan
 *    (`vessels.owner_reference`), seferde isteğe bağlı üzerine yazma (`voyages.owner_reference`).
 *    Spot, armatör seferi ve yat gibi charter'sız seferler içindir.
 * 3. Hiçbiri yoksa referans yok.
 *
 * Veri tek yerde tutulur: CP şartları yalnız CP kaydında; sefer yalnız CP'nin kimliğini taşır.
 * Sonuç etiketi değişmez: "Indicative" (ADR 0084 Ek). Saf fonksiyonlar.
 */

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const optNum = (min: number, max: number) =>
  z
    .number(typed('INVALID_NUMBER'))
    .min(min, 'OUT_OF_RANGE')
    .max(max, 'OUT_OF_RANGE')
    .nullable()
    .default(null);

/** Sahibin referansı: CP şartlarıyla aynı aralıklar (`charterPartyTermsSchema`). */
export const ownerReferenceSchema = z.object({
  speedLadenKn: optNum(0, 40),
  speedBallastKn: optNum(0, 40),
  consumptionLadenMt: optNum(0, 500),
  consumptionBallastMt: optNum(0, 500),
});
export type OwnerReference = z.output<typeof ownerReferenceSchema>;

export const OWNER_REFERENCE_KEYS = [
  'speedLadenKn',
  'speedBallastKn',
  'consumptionLadenMt',
  'consumptionBallastMt',
] as const satisfies readonly (keyof OwnerReference)[];

const issuesOf = (error: z.ZodError): SchemaIssue[] =>
  error.issues.map((i) => ({ path: i.path.map(String).join('.'), code: i.message }));

export function parseOwnerReference(input: unknown): ParseResult<OwnerReference> {
  const result = ownerReferenceSchema.safeParse(input ?? {});
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: issuesOf(result.error) };
}

const anyValue = (terms: Partial<Record<(typeof OWNER_REFERENCE_KEYS)[number], unknown>>) =>
  OWNER_REFERENCE_KEYS.some((key) => typeof terms[key] === 'number');

/**
 * Kayıttaki (JSON) referans; okuma gevşektir: bozuk ya da bütünüyle boş içerik `null` (yazma yolu
 * şemayla denetler, okuma kayıt düşürmez ama geçersiz değeri referans saymaz).
 */
export function ownerReferenceOf(value: unknown): OwnerReference | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const parsed = ownerReferenceSchema.safeParse(value);
  return parsed.success && anyValue(parsed.data) ? parsed.data : null;
}

/* ---------- Seferin CP'si ---------- */

export interface ReferenceCandidate {
  id: string;
  source: CpSource;
  /** Dönüştürülen kaydın kaynak seferi (ADR 0084 §5). */
  voyageId: string | null;
  cpNo: string | null;
  charterer: string | null;
  period: { start: string | null; end: string | null };
  terms: {
    speedLadenKn: number | null;
    speedBallastKn: number | null;
    consumptionLadenMt: number | null;
    consumptionBallastMt: number | null;
  };
}

export type VoyageCpBasis = 'linked' | 'migrated' | 'period';

const covers = (period: { start: string | null; end: string | null }, at: number) =>
  period.start !== null &&
  Date.parse(period.start) <= at &&
  (period.end === null || at < Date.parse(period.end));

/** Verilen anı kapsayan CP dönemlerinden en geç başlayanı (örtüşme register'da işaretli). */
function covering<T extends Pick<ReferenceCandidate, 'period' | 'id'>>(
  items: readonly T[],
  at: number,
): T | null {
  return (
    items
      .filter((item) => covers(item.period, at))
      .sort(
        (a, b) =>
          Date.parse(b.period.start as string) - Date.parse(a.period.start as string) ||
          (a.id < b.id ? 1 : -1),
      )[0] ?? null
  );
}

/**
 * Seferin CP'si: (1) seferin bağladığı CP ("planlanan CP"; ofis Add New Voyage'da seçer ya da
 * açar), (2) bu seferden dönüştürülen CP (ADR 0084 §5), (3) seferin başlangıcını (başlamadıysa
 * planlanan kalkışı) kapsayan CP dönemi (dönem On Hire / Off Hire'dan türer; ADR 0084 §2).
 * Bağlı CP silinmişse listede yoktur; kural sıradakine düşer. Bağ dönemi değiştirmez: CP analizi
 * yine CP döneminin seyirleriyle yapılır.
 */
export function voyageCharterParty<T extends ReferenceCandidate>(
  voyage: {
    id: string;
    charterPartyId: string | null;
    start: string | null;
    plannedEtd: string | null;
  },
  items: readonly T[],
): { item: T; basis: VoyageCpBasis } | null {
  const linked = voyage.charterPartyId
    ? items.find((item) => item.id === voyage.charterPartyId)
    : undefined;
  if (linked) return { item: linked, basis: 'linked' };
  const migrated = items.find((item) => item.source === 'migrated' && item.voyageId === voyage.id);
  if (migrated) return { item: migrated, basis: 'migrated' };
  const from = voyage.start ?? voyage.plannedEtd;
  if (!from) return null;
  const found = covering(items, Date.parse(from));
  return found ? { item: found, basis: 'period' } : null;
}

/** Yeni seferin önerilen CP'si: verilen anı (planlanan kalkış ya da şimdi) kapsayan dönem. */
export function currentCharterParty<T extends ReferenceCandidate>(
  items: readonly T[],
  at: string,
): T | null {
  return covering(items, Date.parse(at));
}

/* ---------- Referans ---------- */

export type ReferenceKind = 'charter_party' | 'owner' | 'none';

export interface PerformanceReference {
  kind: ReferenceKind;
  /** CP'de kaydın kimliği; eski seferin kolonlarından gelen CP'de `null`. */
  charterPartyId: string | null;
  cpNo: string | null;
  charterer: string | null;
  /** Sahibin referansında değerlerin kaynağı: sefer, gemi ya da ikisi (alan alan). */
  ownerSource: 'voyage' | 'vessel' | 'both' | null;
  speedLadenKn: number | null;
  speedBallastKn: number | null;
  consumptionLadenMt: number | null;
  consumptionBallastMt: number | null;
}

const NONE: PerformanceReference = {
  kind: 'none',
  charterPartyId: null,
  cpNo: null,
  charterer: null,
  ownerSource: null,
  speedLadenKn: null,
  speedBallastKn: null,
  consumptionLadenMt: null,
  consumptionBallastMt: null,
};

/**
 * Referans seçimi: CP > Owner's reference > yok. CP'nin hız ve tüketim şartı yoksa (ör. voyage
 * charter) sahibin referansı kullanılır; CP ile sahibin değerleri karıştırılmaz. Sahibin
 * referansında seferin değeri alan alan geminin varsayılanını geçer.
 */
export function performanceReference(input: {
  charterParty:
    | (Pick<ReferenceCandidate, 'cpNo' | 'charterer' | 'terms'> & {
        id: string | null;
      })
    | null;
  voyageOwner: OwnerReference | null;
  vesselOwner: OwnerReference | null;
}): PerformanceReference {
  const cp = input.charterParty;
  if (cp && anyValue(cp.terms)) {
    return {
      kind: 'charter_party',
      charterPartyId: cp.id,
      cpNo: cp.cpNo,
      charterer: cp.charterer,
      ownerSource: null,
      speedLadenKn: cp.terms.speedLadenKn,
      speedBallastKn: cp.terms.speedBallastKn,
      consumptionLadenMt: cp.terms.consumptionLadenMt,
      consumptionBallastMt: cp.terms.consumptionBallastMt,
    };
  }
  const values = { ...NONE };
  let fromVoyage = false;
  let fromVessel = false;
  for (const key of OWNER_REFERENCE_KEYS) {
    const own = input.voyageOwner?.[key] ?? null;
    const fallback = input.vesselOwner?.[key] ?? null;
    if (own !== null) fromVoyage = true;
    else if (fallback !== null) fromVessel = true;
    values[key] = own ?? fallback;
  }
  if (!fromVoyage && !fromVessel) return NONE;
  return {
    ...values,
    kind: 'owner',
    ownerSource: fromVoyage && fromVessel ? 'both' : fromVoyage ? 'voyage' : 'vessel',
  };
}

/** Referansın karşılaştırma terimleri (`cpComparison` girdisi; formül değişmez, ADR 0057 §1). */
export function referenceTerms(reference: PerformanceReference): Required<CpTerms> {
  return {
    cpSpeedLadenKn: reference.speedLadenKn,
    cpSpeedBallastKn: reference.speedBallastKn,
    cpConsumptionLadenMt: reference.consumptionLadenMt,
    cpConsumptionBallastMt: reference.consumptionBallastMt,
  };
}
